<template>
  <div class="income-tax-report">
    <header class="page-header">
      <button class="btn-back" @click="$router.go(-1)">←</button>
      <h1>{{ $t('wholesaler.IncomeTaxReport.title') }}</h1>
      <button class="btn-export" @click="showExportDialog = true">{{ $t('wholesaler.IncomeTaxReport.export') }}</button>
    </header>

    <div class="year-selector">
      <label>{{ $t('wholesaler.IncomeTaxReport.financialYear') }}:</label>
      <select v-model="selectedYear" @change="loadReport">
        <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
      </select>
    </div>

    <div v-if="loading" class="loading">{{ $t('common.loading') }}</div>

    <div v-else class="report-content">
      <!-- 基本信息 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.basicInfo') }}</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.financialYear') }}</span>
            <span class="value">{{ report.year }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.companyName') }}</span>
            <span class="value">{{ report.companyName }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.uen') }}</span>
            <span class="value">{{ report.uen }}</span>
          </div>
        </div>
      </div>

      <!-- 缺失数据提示 -->
      <div v-if="report.hasMissingMonths" class="section-card warning">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.dataIncomplete') }}</div>
        <p class="hint-text">{{ getMissingDataHint() }}</p>
        <button class="btn-primary" @click="openHistoricalDialog">
          {{ $t('wholesaler.IncomeTaxReport.supplementHistory') }}
        </button>
      </div>

      <!-- 收入汇总 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.revenueSummary') }}</div>
        <div class="data-table">
          <div class="table-row">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.turnover') }}</span>
            <span class="value">S$ {{ formatMoney(report.totalRevenue) }}</span>
            <span class="auto-tag">{{ $t('wholesaler.IncomeTaxReport.auto') }}</span>
          </div>
          <div class="table-row">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.otherIncome') }}</span>
            <span class="value">S$ {{ formatMoney(report.otherIncome) }}</span>
            <span class="manual-tag">{{ $t('wholesaler.IncomeTaxReport.manual') }}</span>
          </div>
          <div class="table-row total">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.totalIncome') }}</span>
            <span class="value">S$ {{ formatMoney(report.totalIncome) }}</span>
          </div>
        </div>
      </div>

      <!-- 扣除项目 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.deductions') }}</div>
        <div class="deduction-list">
          <div v-for="item in report.deductions" :key="item.type" class="deduction-item">
            <div class="deduction-header">
              <span class="deduction-label">{{ getDeductionLabel(item.type) }}</span>
              <span :class="item.autoCalc ? 'auto-tag' : 'manual-tag'">
                {{ item.autoCalc ? $t('wholesaler.IncomeTaxReport.autoCalc') : $t('wholesaler.IncomeTaxReport.manualFill') }}
              </span>
            </div>
            <div class="deduction-input">
              <span class="currency">S$</span>
              <input
                v-if="!item.autoCalc"
                type="number"
                v-model="item.amount"
                @input="recalculate"
                step="0.01"
                min="0"
                class="amount-input"
              />
              <span v-else class="amount-display">{{ formatMoney(item.amount) }}</span>
            </div>
            <div v-if="getDeductionRemark(item)" class="deduction-remark">{{ getDeductionRemark(item) }}</div>
          </div>
        </div>
        <div class="data-table">
          <div class="table-row total">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.totalDeductions') }}</span>
            <span class="value">S$ {{ formatMoney(report.totalDeductions) }}</span>
          </div>
        </div>
      </div>

      <!-- 应课税收入计算 -->
      <div class="section-card highlight">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.chargeableIncomeCalc') }}</div>
        <div class="data-table">
          <div class="table-row">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.totalIncome') }}</span>
            <span class="value">S$ {{ formatMoney(report.totalIncome) }}</span>
          </div>
          <div class="table-row">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.totalDeductions') }}</span>
            <span class="value">S$ {{ formatMoney(report.totalDeductions) }}</span>
          </div>
          <div class="table-row total">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.chargeableIncome') }}</span>
            <span class="value">S$ {{ formatMoney(report.chargeableIncome) }}</span>
          </div>
        </div>
      </div>

      <!-- 税务减免 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.taxExemption') }}</div>
        <div class="data-table">
          <div class="table-row" v-for="item in report.taxExemptions" :key="item.type">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.partialExemption') }}</span>
            <span class="value">S$ {{ formatMoney(item.amount) }}</span>
          </div>
          <div class="table-row total">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.taxableIncome') }}</span>
            <span class="value">S$ {{ formatMoney(report.taxableIncome) }}</span>
          </div>
        </div>
      </div>

      <!-- 应缴税额 -->
      <div class="section-card highlight">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.taxPayable') }}</div>
        <div class="data-table">
          <div class="table-row">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.taxableIncome') }}</span>
            <span class="value">S$ {{ formatMoney(report.taxableIncome) }}</span>
          </div>
          <div class="table-row">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.taxRate') }}</span>
            <span class="value">17%</span>
          </div>
          <div class="table-row total">
            <span class="label">{{ $t('wholesaler.IncomeTaxReport.taxPayable') }}</span>
            <span class="value">S$ {{ formatMoney(report.taxPayable) }}</span>
          </div>
        </div>
      </div>

      <!-- 汇报文案 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.reportSummary') }}</div>
        <div class="report-text">
          <p><strong>{{ $t('wholesaler.IncomeTaxReport.annualSummary') }} ({{ report.year }})</strong></p>
          <p>{{ report.summary }}</p>
        </div>
        <button class="btn-copy" @click="copySummary">{{ $t('wholesaler.IncomeTaxReport.copySummary') }}</button>
      </div>

      <!-- 重要提示 -->
      <div class="section-card warning">
        <div class="section-title">{{ $t('wholesaler.IncomeTaxReport.importantNotes') }}</div>
        <ul class="reminder-list">
          <li>{{ $t('wholesaler.IncomeTaxReport.note1') }}</li>
          <li>{{ $t('wholesaler.IncomeTaxReport.note2') }}</li>
          <li>{{ $t('wholesaler.IncomeTaxReport.note3', { year: report.year + 1 }) }}</li>
          <li>{{ $t('wholesaler.IncomeTaxReport.note4') }}</li>
        </ul>
      </div>
    </div>

    <!-- 导出对话框 -->
    <div v-if="showExportDialog" class="modal-overlay modal-show" @click="showExportDialog = false">
      <div class="modal-content" @click.stop>
        <h3>{{ $t('wholesaler.IncomeTaxReport.exportReport') }}</h3>
        <div class="export-options">
          <button @click="exportReport('excel')" class="btn-option">
            <span class="icon">📊</span>
            <span>{{ $t('wholesaler.IncomeTaxReport.excelFormat') }}</span>
          </button>
        </div>
        <button class="btn-cancel" @click="showExportDialog = false">{{ $t('wholesaler.IncomeTaxReport.cancel') }}</button>
      </div>
    </div>

    <!-- 历史数据补充对话框 -->
    <div v-if="showHistoricalDialog" class="modal-overlay modal-show" @click="closeHistoricalDialog">
      <div class="modal-content modal-large" @click.stop>
        <h3>{{ $t('wholesaler.IncomeTaxReport.supplementHistoryData', { year: selectedYear }) }}</h3>
        <p class="dialog-hint">{{ $t('wholesaler.IncomeTaxReport.historyHint') }}</p>
        
        <div class="historical-form">
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalRevenue') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalRevenue" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalCostOfSales') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalCostOfSales" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalWastage') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalWastage" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalStaffCost') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalStaffCost" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalRental') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalRental" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalTransport') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalTransport" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalUtilities') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalUtilities" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalDepreciation') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalDepreciation" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.IncomeTaxReport.historicalOthers') }}</label>
            <div class="input-with-prefix">
              <span class="prefix">S$</span>
              <input type="number" v-model="historicalData.historicalOthers" step="0.01" min="0" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group full-width">
            <label>{{ $t('wholesaler.IncomeTaxReport.remark') }}</label>
            <textarea v-model="historicalData.remark" :placeholder="$t('wholesaler.IncomeTaxReport.remarkPlaceholder')" rows="2"></textarea>
          </div>
        </div>

        <div class="dialog-actions">
          <button v-if="historicalDataId" class="btn-danger" @click="confirmDelete">{{ $t('wholesaler.IncomeTaxReport.delete') }}</button>
          <div class="right-actions">
            <button class="btn-cancel" @click="closeHistoricalDialog">{{ $t('wholesaler.IncomeTaxReport.cancel') }}</button>
            <button class="btn-primary" @click="saveHistoricalData" :disabled="saving">
              {{ saving ? $t('wholesaler.IncomeTaxReport.saving') : $t('wholesaler.IncomeTaxReport.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAnnualIncomeTaxReport, exportIncomeTaxReport, getTaxHistoricalData, createTaxHistoricalData, updateTaxHistoricalData, deleteTaxHistoricalData } from '@/api/tax'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'IncomeTaxReport',
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      years: [],
      report: {
        year: 0,
        companyName: '',
        uen: '',
        totalRevenue: 0,
        otherIncome: 0,
        totalIncome: 0,
        deductions: [],
        totalDeductions: 0,
        chargeableIncome: 0,
        taxExemptions: [],
        taxableIncome: 0,
        taxPayable: 0,
        summary: ''
      },
      loading: false,
      showExportDialog: false,
      showHistoricalDialog: false,
      saving: false,
      historicalDataId: null,
      historicalData: {
        historicalRevenue: null,
        historicalCostOfSales: null,
        historicalWastage: null,
        historicalStaffCost: null,
        historicalRental: null,
        historicalTransport: null,
        historicalUtilities: null,
        historicalDepreciation: null,
        historicalOthers: null,
        remark: ''
      }
    }
  },
  created() {
    this.initYears()
    this.loadReport()
  },
  methods: {
    initYears() {
      const currentYear = new Date().getFullYear()
      this.years = Array.from({ length: 5 }, (_, i) => currentYear - i)
    },
    async loadReport() {
      this.loading = true
      try {
        const wholesalerId = getWholesalerId()
        const response = await getAnnualIncomeTaxReport(this.selectedYear, wholesalerId)
        this.report = response.data
        // 生成汇报文案（使用当前语言）
        this.generateSummary()
      } catch (error) {
        console.error('Load report failed:', error)
        this.$toast.error(this.$t('wholesaler.IncomeTaxReport.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    recalculate() {
      // 重新计算总扣除
      this.report.totalDeductions = this.report.deductions.reduce(
        (sum, item) => sum + (parseFloat(item.amount) || 0),
        0
      )

      // 重新计算应课税收入
      this.report.chargeableIncome = this.report.totalIncome - this.report.totalDeductions
      if (this.report.chargeableIncome < 0) {
        this.report.chargeableIncome = 0
      }

      // 重新计算税务减免
      this.calculateTaxExemptions()

      // 重新计算应缴税额
      this.calculateTaxPayable()

      // 重新生成汇报文案
      this.generateSummary()
    },
    calculateTaxExemptions() {
      this.report.taxExemptions = []
      
      if (this.report.chargeableIncome <= 0) {
        this.report.taxableIncome = 0
        return
      }

      let exemptionAmount = 0

      // 首 S$10,000: 75% 免税
      const first10k = Math.min(this.report.chargeableIncome, 10000)
      exemptionAmount += first10k * 0.75

      // 次 S$190,000: 50% 免税
      if (this.report.chargeableIncome > 10000) {
        const next190k = Math.min(this.report.chargeableIncome - 10000, 190000)
        exemptionAmount += next190k * 0.50
      }

      if (exemptionAmount > 0) {
        this.report.taxExemptions.push({
          type: 'PARTIAL_EXEMPTION',
          amount: exemptionAmount
        })
      }

      // 计算应税收入
      const totalExemption = this.report.taxExemptions.reduce(
        (sum, item) => sum + item.amount,
        0
      )
      this.report.taxableIncome = Math.max(0, this.report.chargeableIncome - totalExemption)
    },
    calculateTaxPayable() {
      this.report.taxPayable = this.report.taxableIncome * 0.17
    },
    generateSummary() {
      const year = this.report.year
      const ti = this.formatMoney(this.report.totalIncome)
      const td = this.formatMoney(this.report.totalDeductions)
      const ci = this.formatMoney(this.report.chargeableIncome)
      const tp = this.formatMoney(this.report.taxPayable)
      this.report.summary = this.$t('wholesaler.IncomeTaxReport.summary', {
        totalIncome: ti,
        totalDeductions: td,
        chargeableIncome: ci,
        taxPayable: tp,
        year: year + 1
      })
    },
    getDeductionLabel(type) {
      const key = `wholesaler.IncomeTaxReport.deductionLabels.${type}`
      return this.$te(key) ? this.$t(key) : type
    },
    getDeductionRemark(item) {
      if (item.hasHistoricalData && item.amount > 0) {
        return this.$t('wholesaler.IncomeTaxReport.hasHistoryRemark', { amount: this.formatMoney(item.amount) })
      }
      if (!item.autoCalc) {
        const key = `wholesaler.IncomeTaxReport.deductionRemarks.${item.type}`
        return this.$te(key) ? this.$t(key) : ''
      }
      if (item.type === 'WASTAGE') {
        return this.$t('wholesaler.IncomeTaxReport.deductionRemarks.WASTAGE')
      }
      return ''
    },
    getMissingDataHint() {
      if (!this.report.missingMonths || this.report.missingMonths.length === 0) {
        return this.$t('wholesaler.IncomeTaxReport.missingDataHintFull')
      }
      const monthNames = this.$t('wholesaler.IncomeTaxReport.months')
      const months = this.report.missingMonths.map(m => monthNames[m - 1]).join(', ')
      // Check if continuous from start
      const lastMissing = this.report.missingMonths[this.report.missingMonths.length - 1]
      let isContinuous = true
      for (let m = 1; m <= lastMissing; m++) {
        if (!this.report.missingMonths.includes(m)) { isContinuous = false; break }
      }
      if (isContinuous && this.report.missingMonths.length < 12) {
        return this.$t('wholesaler.IncomeTaxReport.missingDataHintContinuous', { startMonth: monthNames[lastMissing], months })
      }
      return this.$t('wholesaler.IncomeTaxReport.missingDataHintScattered', { months })
    },
    formatMoney(amount) {
      if (!amount) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    async exportReport(format) {
      try {
        const wholesalerId = getWholesalerId()
        const lang = this.$i18n.locale
        const response = await exportIncomeTaxReport(this.selectedYear, wholesalerId, format, lang)
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `Income_Tax_Report_${this.selectedYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`
        link.click()
        window.URL.revokeObjectURL(url)
        this.showExportDialog = false
        this.$toast.success(this.$t('wholesaler.IncomeTaxReport.exportSuccess'))
      } catch (error) {
        console.error('Export failed:', error)
        this.$toast.error(this.$t('wholesaler.IncomeTaxReport.exportFailed'))
      }
    },
    copySummary() {
      const text = this.report.summary
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success(this.$t('wholesaler.IncomeTaxReport.copySuccess'))
      }).catch(() => {
        this.$toast.error(this.$t('wholesaler.IncomeTaxReport.copyFailed'))
      })
    },
    async openHistoricalDialog() {
      this.showHistoricalDialog = true
      const wholesalerId = getWholesalerId()
      if (!wholesalerId) {
        console.warn('⚠️ 无法获取批发商ID，历史数据可能无法保存')
        this.historicalDataId = null
        this.resetHistoricalData()
        return
      }
      try {
        const res = await getTaxHistoricalData(wholesalerId, this.selectedYear)
        if (res.data && res.data.data) {
          const d = res.data.data
          this.historicalDataId = d.id
          this.historicalData = {
            historicalRevenue: d.historicalRevenue,
            historicalCostOfSales: d.historicalCostOfSales,
            historicalWastage: d.historicalWastage,
            historicalStaffCost: d.historicalStaffCost,
            historicalRental: d.historicalRental,
            historicalTransport: d.historicalTransport,
            historicalUtilities: d.historicalUtilities,
            historicalDepreciation: d.historicalDepreciation,
            historicalOthers: d.historicalOthers,
            remark: d.remark || ''
          }
        } else {
          this.historicalDataId = null
          this.resetHistoricalData()
        }
      } catch (e) {
        console.error('加载历史数据失败:', e)
        this.historicalDataId = null
        this.resetHistoricalData()
      }
    },
    resetHistoricalData() {
      this.historicalData = {
        historicalRevenue: null,
        historicalCostOfSales: null,
        historicalWastage: null,
        historicalStaffCost: null,
        historicalRental: null,
        historicalTransport: null,
        historicalUtilities: null,
        historicalDepreciation: null,
        historicalOthers: null,
        remark: ''
      }
    },
    closeHistoricalDialog() {
      this.showHistoricalDialog = false
    },
    async saveHistoricalData() {
      const wholesalerId = getWholesalerId()
      if (!wholesalerId) {
        this.$toast.error('Cannot get wholesaler info, please login again')
        return
      }
      this.saving = true
      const payload = {
        wholesalerId: Number(wholesalerId),
        year: this.selectedYear,
        coveredMonths: this.report.coveredMonths || [],
        ...this.historicalData
      }
      try {
        if (this.historicalDataId) {
          await updateTaxHistoricalData(this.historicalDataId, payload)
        } else {
          await createTaxHistoricalData(payload)
        }
        this.$toast.success(this.$t('wholesaler.IncomeTaxReport.saveSuccess'))
        this.closeHistoricalDialog()
        this.loadReport()
      } catch (e) {
        this.$toast.error(this.$t('wholesaler.IncomeTaxReport.saveFailed') + ': ' + (e.response?.data?.message || e.message))
      } finally {
        this.saving = false
      }
    },
    async confirmDelete() {
      if (!confirm(this.$t('wholesaler.IncomeTaxReport.confirmDelete'))) return
      try {
        await deleteTaxHistoricalData(this.historicalDataId)
        this.$toast.success(this.$t('wholesaler.IncomeTaxReport.deleteSuccess'))
        this.closeHistoricalDialog()
        this.loadReport()
      } catch (e) {
        this.$toast.error(this.$t('wholesaler.IncomeTaxReport.deleteFailed'))
      }
    }
  }
}
</script>

<style scoped>
.income-tax-report {
  padding: 16px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  margin-bottom: 16px;
}

.btn-back {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
}

.page-header h1 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.btn-export {
  background: #1890ff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.year-selector {
  background: white;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.year-selector label {
  font-weight: 500;
}

.year-selector select {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #999;
}

.report-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
}

.section-card.highlight {
  background: #e6f7ff;
  border: 1px solid #91d5ff;
}

.section-card.warning {
  background: #fff7e6;
  border: 1px solid #ffd591;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item .label {
  font-size: 12px;
  color: #666;
}

.info-item .value {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.data-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.table-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.table-row.total {
  border-bottom: none;
  border-top: 2px solid #333;
  margin-top: 8px;
  padding-top: 12px;
  font-weight: 600;
  font-size: 16px;
}

.table-row .label {
  color: #666;
  font-size: 14px;
}

.table-row .value {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.table-row.total .value {
  color: #1890ff;
  font-size: 18px;
}

.auto-tag, .manual-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
}

.auto-tag {
  background: #e6f7ff;
  color: #1890ff;
}

.manual-tag {
  background: #fff7e6;
  color: #fa8c16;
}

.deduction-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.deduction-item {
  padding: 12px;
  background: #fafafa;
  border-radius: 4px;
}

.deduction-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.deduction-label {
  font-weight: 500;
  color: #333;
}

.deduction-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.currency {
  color: #666;
  font-size: 14px;
}

.amount-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
}

.amount-display {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.deduction-remark {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
}

.report-text {
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.6;
}

.report-text p {
  margin: 8px 0;
}

.btn-copy {
  background: #52c41a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  width: 100%;
}

.reminder-list {
  margin: 0;
  padding-left: 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.8;
}

.reminder-list li {
  margin-bottom: 8px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.modal-content h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
}

.export-options {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.btn-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.btn-option:hover {
  border-color: #1890ff;
  background: #e6f7ff;
}

.btn-option .icon {
  font-size: 32px;
}

.btn-cancel {
  width: 100%;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.hint-text {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 12px;
}

.btn-primary {
  background: #1890ff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-danger {
  background: #ff4d4f;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.modal-large {
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.dialog-hint {
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
}

.historical-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 13px;
  color: #666;
}

.input-with-prefix {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
}

.input-with-prefix .prefix {
  padding: 8px 10px;
  background: #fafafa;
  color: #666;
  font-size: 13px;
  border-right: 1px solid #d9d9d9;
}

.input-with-prefix input {
  flex: 1;
  padding: 8px 10px;
  border: none;
  outline: none;
  font-size: 14px;
}

.form-group textarea {
  padding: 8px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
}

.dialog-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.right-actions {
  display: flex;
  gap: 8px;
}

.right-actions .btn-cancel {
  width: auto;
  padding: 8px 16px;
}
</style>
